import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronRight, Bot, BarChart2, Sparkles, Flag } from 'lucide-react';


interface DigitalScoreCtaProps {
  className?: string;
  onClick?: () => void;
  companyName?: string;
}

const DigitalScoreCta: React.FC<DigitalScoreCtaProps> = ({
  className = '',
  onClick,
  companyName
}) => {
  // Contenu fixe du CTA
  const content = {
    title: "Envie d'aller plus loin ?",
    subtitle: "Affinez votre feuille de route digitale en répondant à quelques questions clés."
  };
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`relative py-12 px-6 text-center ${className}`}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-indigo-50/30 pointer-events-none" />
      
      {/* Floating elements for visual interest */}
      <motion.div 
        className="absolute top-8 left-1/4 w-2 h-2 bg-blue-400/40 rounded-full"
        animate={{ 
          y: [0, -10, 0],
          opacity: [0.4, 0.8, 0.4]
        }}
        transition={{ 
          duration: 3, 
          repeat: Infinity,
          repeatType: "reverse"
        }}
      />
      <motion.div 
        className="absolute top-16 right-1/3 w-1.5 h-1.5 bg-indigo-400/50 rounded-full"
        animate={{ 
          y: [0, -8, 0],
          opacity: [0.3, 0.7, 0.3]
        }}
        transition={{ 
          duration: 4, 
          repeat: Infinity,
          repeatType: "reverse",
          delay: 1
        }}
      />
      
      <div className="relative z-10 max-w-2xl mx-auto">
        {/* Icon with company name */}
        <motion.div 
          className="flex items-center justify-center gap-3 mb-6"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <motion.div 
            className="flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg"
            whileHover={{ scale: 1.05, rotate: 5 }}
            animate={{ 
              boxShadow: [
                "0 4px 6px -1px rgba(59, 130, 246, 0.3)",
                "0 8px 12px -2px rgba(59, 130, 246, 0.4)",
                "0 4px 6px -1px rgba(59, 130, 246, 0.3)"
              ]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              repeatType: "reverse" 
            }}
          >
            <BarChart2 className="w-6 h-6" />
          </motion.div>
          
          {companyName && (
            <motion.span 
              className="text-lg font-semibold text-gray-700"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              {companyName}
            </motion.span>
          )}
        </motion.div>
        
        {/* Main title */}
        <motion.h2 
          className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          {content.title}
        </motion.h2>
        
        {/* Subtitle */}
        <motion.p 
          className="text-lg text-gray-600 mb-8 leading-relaxed"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          {content.subtitle}
        </motion.p>
        
        {/* Features badges */}
        <motion.div 
          className="flex flex-wrap justify-center gap-4 mb-10"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          {[
            { icon: <Calendar className="w-4 h-4" />, text: "Depuis 2018", color: "blue" },
            { icon: <Bot className="w-4 h-4" />, text: "Certifié IA", color: "green" },
            { icon: <Flag className="w-4 h-4" />, text: "Swiss Made", color: "red" }
          ].map((feature, index) => (
            <motion.div 
              key={index}
              className={`flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-${feature.color}-100 text-sm font-medium text-gray-700`}
              whileHover={{ 
                scale: 1.05, 
                boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.1)" 
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
            >
              <div className={`text-${feature.color}-600`}>
                {feature.icon}
              </div>
              <span>{feature.text}</span>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Main CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="relative"
        >
          {/* Glow effect behind button */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur-lg opacity-30"
            animate={{ 
              scale: [1, 1.05, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              repeatType: "reverse" 
            }}
          />
          
          <motion.button
            className="relative group flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-lg py-4 px-8 rounded-2xl shadow-lg transition-all duration-300 mx-auto"
            whileHover={{ 
              scale: 1.02, 
              boxShadow: "0 20px 25px -5px rgba(59, 130, 246, 0.3), 0 10px 10px -5px rgba(59, 130, 246, 0.1)" 
            }}
            whileTap={{ scale: 0.98 }}
            onClick={handleClick}
          >
            <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
            <span>Obtenir mon score en 1 minute</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            
            {/* Button shine effect */}
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-2xl"
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatDelay: 3,
                ease: "easeInOut"
              }}
            />
          </motion.button>
        </motion.div>
        
        {/* Trust indicators */}
        <motion.div 
          className="flex justify-center items-center mt-6 space-x-6 text-sm text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.5 }}
        >
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            Sans engagement
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            Données sécurisées
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            Résultats immédiats
          </span>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default DigitalScoreCta;
